/**
 * An edit, read as a diff rather than as two JSON strings.
 *
 * A tool call that changed a file currently shows its arguments: `old_string`
 * and `new_string`, JSON-escaped, one after the other. That is the raw truth and
 * it is unreadable — the one question you have about an edit ("what actually
 * changed on that line?") is the one thing two adjacent blobs cannot answer,
 * because the eye has to diff them itself.
 *
 * So the arguments become a diff. Same information, arranged the way every other
 * tool in the trade arranges it: removed lines, added lines, a little context
 * either side, long unchanged runs elided.
 *
 * RECOGNISED BY SHAPE, NOT BY NAME. Tools arrive from MCP servers and from apps
 * this file has never heard of, and a table of known tool names would silently
 * degrade to raw JSON for every one of them (the same reasoning `toolSummary`
 * gives for its generic rule). An edit is anything carrying an old text and a
 * new text, whatever it calls itself; a write is a path plus content from a tool
 * whose name says it writes.
 *
 * NO LINE NUMBERS. A replacement snippet's position in the file is not in the
 * arguments, so any number shown here would be invented. The gutter carries the
 * sign only, which is what it can honestly say.
 */

import { argString } from './toolArgs'

export type DiffRowType = 'context' | 'added' | 'removed' | 'gap'

export interface DiffRow {
  type: DiffRowType
  /** The line, or for a gap the count of lines it stands in for. */
  text: string
}

export interface EditPreview {
  /** The file the call named, in full, for a title attribute. */
  path: string
  /** The last two segments, for a row that has to stay one line wide. */
  label: string
  rows: DiffRow[]
  added: number
  removed: number
  /** Rows were dropped to keep the block from becoming a wall. */
  truncated: boolean
  /** A whole new file, rather than a replacement inside an existing one. */
  whole: boolean
}

/** Argument names for the text being replaced, and for its replacement. */
const OLD_KEYS = [
  'old_string',
  'oldString',
  'old_str',
  'old_text',
  'oldText',
  'search',
]
const NEW_KEYS = [
  'new_string',
  'newString',
  'new_str',
  'new_text',
  'newText',
  'replace',
]
const PATH_KEYS = [
  'file_path',
  'filePath',
  'path',
  'notebook_path',
  'notebookPath',
]
/** Whole-file bodies. Only trusted when the tool's name says it writes. */
const CONTENT_KEYS = ['content', 'contents', 'new_source', 'source']
/** Argument names that hold a LIST of replacements (MultiEdit and friends). */
const BATCH_KEYS = ['edits', 'replacements', 'operations', 'changes']

/** A tool whose name claims it creates or overwrites a file. */
const WRITES = /write|create|overwrite/i

/**
 * Cheap guard on the LCS table. 250k cells is a 500x500 edit, far past anything
 * worth reading line by line; beyond it the block form (all removals, then all
 * additions) is both correct and what a person would want anyway.
 */
const MAX_CELLS = 250_000

/** Unchanged lines kept either side of a change. */
const CONTEXT = 3

/** Rows rendered before the block gives up and says how many it dropped. */
const MAX_ROWS = 240

function pickString(
  record: Record<string, unknown>,
  keys: string[],
): string | undefined {
  for (const key of keys) {
    // Through `argString`, because a string off the log is an ImmutableString
    // wrapper and not a string. See `toolArgs`.
    //
    // Presence is "is it textual", never truthiness: an empty old_string is an
    // INSERTION, and treating it as absent turns a real edit back into raw JSON.
    const value = argString(record[key])
    if (value !== undefined) return value
  }
  return undefined
}

interface Replacement {
  old: string
  new: string
}

/** Every replacement the arguments describe, top-level one first. */
function replacementsOf(record: Record<string, unknown>): Replacement[] {
  const out: Replacement[] = []
  const push = (candidate: unknown) => {
    if (!candidate || typeof candidate !== 'object') return
    const entry = candidate as Record<string, unknown>
    const oldText = pickString(entry, OLD_KEYS)
    const newText = pickString(entry, NEW_KEYS)
    if (oldText === undefined && newText === undefined) return
    out.push({ old: oldText ?? '', new: newText ?? '' })
  }
  push(record)
  for (const key of BATCH_KEYS) {
    const batch = record[key]
    if (Array.isArray(batch)) for (const entry of batch) push(entry)
  }
  return out
}

/**
 * Split a file body into lines, dropping the empty element a trailing newline
 * leaves behind. Without this every whole-file write reports one more line than
 * it has, and every edit ending in a newline shows a phantom blank change.
 */
function toLines(text: string): string[] {
  if (text.length === 0) return []
  const lines = text.replace(/\r\n/g, '\n').split('\n')
  if (lines.length > 1 && lines[lines.length - 1] === '') lines.pop()
  return lines
}

/**
 * A line-level diff by longest common subsequence.
 *
 * The table holds LCS lengths from each (i, j) to the end, so the walk forward
 * can choose between dropping a line from the left and taking one from the
 * right by comparing two cells. Typed array rather than nested arrays: an edit
 * of a few hundred lines each way is 250k numbers, and boxing them is the
 * difference between instant and noticeable in a thread that renders many.
 */
function diffLines(before: string[], after: string[]): DiffRow[] {
  if (before.length === 0 && after.length === 0) return []
  if (
    before.length === 0 ||
    after.length === 0 ||
    before.length * after.length > MAX_CELLS
  ) {
    return [
      ...before.map((text) => ({ type: 'removed' as const, text })),
      ...after.map((text) => ({ type: 'added' as const, text })),
    ]
  }

  const width = after.length + 1
  const lcs = new Uint32Array((before.length + 1) * width)
  for (let i = before.length - 1; i >= 0; i--) {
    for (let j = after.length - 1; j >= 0; j--) {
      lcs[i * width + j] =
        before[i] === after[j]
          ? lcs[(i + 1) * width + j + 1] + 1
          : Math.max(lcs[(i + 1) * width + j], lcs[i * width + j + 1])
    }
  }

  const rows: DiffRow[] = []
  let i = 0
  let j = 0
  while (i < before.length && j < after.length) {
    if (before[i] === after[j]) {
      rows.push({ type: 'context', text: before[i] })
      i++
      j++
    } else if (lcs[(i + 1) * width + j] >= lcs[i * width + j + 1]) {
      rows.push({ type: 'removed', text: before[i] })
      i++
    } else {
      rows.push({ type: 'added', text: after[j] })
      j++
    }
  }
  while (i < before.length) rows.push({ type: 'removed', text: before[i++] })
  while (j < after.length) rows.push({ type: 'added', text: after[j++] })
  return groupRuns(rows)
}

/**
 * Within each run of changed lines, put every removal before every addition.
 *
 * The walk above emits them in whatever order the table led it through, so a
 * two-line change can come out `- + - +`, which reads as four separate edits
 * rather than one. Every diff tool in existence groups them, and a reader's eye
 * depends on it: the old block above, the new block below.
 */
function groupRuns(rows: DiffRow[]): DiffRow[] {
  const out: DiffRow[] = []
  let run: DiffRow[] = []
  const flush = () => {
    if (run.length === 0) return
    for (const row of run) if (row.type === 'removed') out.push(row)
    for (const row of run) if (row.type === 'added') out.push(row)
    run = []
  }
  for (const row of rows) {
    if (row.type === 'context') {
      flush()
      out.push(row)
    } else {
      run.push(row)
    }
  }
  flush()
  return out
}

/**
 * Elide unchanged runs longer than twice the context, replacing them with a
 * single gap row. A whole-file write has no context at all, so this is a no-op
 * there and does its work on the replacement edits, where the agent routinely
 * passes thirty unchanged lines to anchor a two-line change.
 */
function elideContext(rows: DiffRow[]): DiffRow[] {
  const out: DiffRow[] = []
  let i = 0
  while (i < rows.length) {
    if (rows[i].type !== 'context') {
      out.push(rows[i])
      i++
      continue
    }
    let end = i
    while (end < rows.length && rows[end].type === 'context') end++
    const run = end - i
    // A leading or trailing run keeps context on ONE side only: there is no
    // change above the first run or below the last, so the far half is padding.
    const head = i === 0 ? 0 : CONTEXT
    const tail = end === rows.length ? 0 : CONTEXT
    if (run <= head + tail + 1) {
      for (let k = i; k < end; k++) out.push(rows[k])
    } else {
      for (let k = i; k < i + head; k++) out.push(rows[k])
      const hidden = run - head - tail
      out.push({
        type: 'gap',
        text: hidden === 1 ? '1 unchanged line' : `${hidden} unchanged lines`,
      })
      for (let k = end - tail; k < end; k++) out.push(rows[k])
    }
    i = end
  }
  return out
}

/** The last two path segments: enough to tell two `index.ts` apart. */
function shortLabel(path: string): string {
  const parts = path.split('/').filter(Boolean)
  return parts.slice(-2).join('/') || path
}

interface Recognised {
  path: string
  replacements: Replacement[]
  whole: boolean
}

/** What the arguments describe, before any of it is diffed. */
function recognise(name: string, args: unknown): Recognised | null {
  if (!args || typeof args !== 'object' || Array.isArray(args)) return null
  const record = args as Record<string, unknown>
  const path = pickString(record, PATH_KEYS) ?? ''

  const replacements = replacementsOf(record)
  if (replacements.length === 0) {
    // No old/new pair. A whole-file write qualifies, but only on the strength of
    // BOTH a path and a name that says it writes: plenty of tools carry a
    // `content` argument (a message, a document body) and are not file edits.
    const content = pickString(record, CONTENT_KEYS)
    if (content === undefined || path === '' || !WRITES.test(name)) return null
    return { path, replacements: [{ old: '', new: content }], whole: true }
  }
  return { path, replacements, whole: false }
}

/**
 * Did this call change a file?
 *
 * The same question `describeEdit` answers, without paying for the answer.
 * `threadRows` asks it of every call in a transcript to decide which ones leave
 * the run (RFC 0298 §4f), and running an LCS per call to find out would make
 * laying out the thread cost what rendering every diff in it costs.
 *
 * Exactly equivalent to `describeEdit(...) !== null`, and the comparison is on
 * LINES rather than the raw strings to keep it that way: `'a\n'` and `'a'` are
 * different strings and the same single line, so comparing the text would call
 * that an edit while `describeEdit` correctly finds nothing in it.
 */
export function isEdit(name: string, args: unknown): boolean {
  const found = recognise(name, args)
  if (!found) return false
  return found.replacements.some(
    (r) => toLines(r.old).join('\n') !== toLines(r.new).join('\n'),
  )
}

/**
 * The diff for a tool call, or null when the call did not edit a file — in
 * which case the caller falls back to showing arguments as it always has.
 */
export function describeEdit(name: string, args: unknown): EditPreview | null {
  const found = recognise(name, args)
  if (!found) return null
  const { path, replacements, whole } = found

  let rows: DiffRow[] = []
  let added = 0
  let removed = 0
  for (const replacement of replacements) {
    const part = diffLines(toLines(replacement.old), toLines(replacement.new))
    for (const row of part) {
      if (row.type === 'added') added++
      else if (row.type === 'removed') removed++
    }
    // A separator between replacements, so a MultiEdit does not read as one
    // continuous change with the pieces welded together.
    if (rows.length > 0) rows.push({ type: 'gap', text: '' })
    rows = rows.concat(elideContext(part))
  }

  if (added === 0 && removed === 0) return null

  const truncated = rows.length > MAX_ROWS
  if (truncated) rows = rows.slice(0, MAX_ROWS)

  return {
    path,
    label: shortLabel(path),
    rows,
    added,
    removed,
    truncated,
    whole,
  }
}
